import axios from 'axios';
import { API_BASE_URL } from '../config/api';

class ProgramPGYUtils {
  /**
   * Get PGY level options for a program
   * @param {number} programId - The program ID
   * @returns {Promise<Array<{value: number, label: string}>>} - Array of PGY options
   */
  static async getPGYLevelOptions(programId) {
    try {
      console.log('ProgramPGYUtils: Making API call to:', `${API_BASE_URL}/api/programs/${programId}/pgy-levels`);
      
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/programs/${programId}/pgy-levels`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('ProgramPGYUtils: API response:', response.data);
      if (response.data.success) {
        return response.data.pgyLevels;
      }
      throw new Error(response.data.message || 'Failed to get PGY levels');
    } catch (error) {
      console.error('ProgramPGYUtils: Error getting PGY levels:', error);
      console.error('ProgramPGYUtils: Error details:', error.response?.data || error.message);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('ProgramPGYUtils: Using fallback options:', fallbackOptions);
      return fallbackOptions;
    }
  }

  /**
   * Get maximum PGY level for a program
   * @param {number} programId - The program ID
   * @returns {Promise<number>} - The maximum PGY level
   */
  static async getMaxPGYLevel(programId) {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/programs/${programId}/max-pgy-level`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        return response.data.maxPGYLevel;
      }
      throw new Error(response.data.message || 'Failed to get max PGY level');
    } catch (error) {
      console.error('Error getting max PGY level:', error);
      return 5; // Default fallback
    }
  }

  /**
   * Generate PGY level options for a given maximum level
   * @param {number} maxLevel - The maximum PGY level
   * @returns {Array<{value: number, label: string}>} - Array of PGY options
   */
  static generatePGYOptions(maxLevel) {
    return Array.from({ length: maxLevel }, (_, i) => ({
      value: i + 1,
      label: `PGY-${i + 1}`
    }));
  }

  /**
   * Check if a PGY level is valid for a given maximum level
   * @param {number} pgyLevel - The PGY level to check
   * @param {number} maxLevel - The maximum PGY level
   * @returns {boolean} - Whether the PGY level is valid
   */
  static isValidPGYLevel(pgyLevel, maxLevel) {
    return pgyLevel >= 1 && pgyLevel <= maxLevel;
  }
}

export default ProgramPGYUtils; 